var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { fsCheckboxDefaults } from '@cof/fs-checkbox-core/IFsCheckbox.js';
import checkboxStyles from '@cof/fs-checkbox-core/lib/checkbox.lit.min.js';
import labelStyles from '@cof/fs-field-label-core/lib/field-label.lit.min.js';
import { FormControlMixin } from '@cof/fs-form-control';
import formFieldStyles from '@cof/fs-form-field-core/lib/form-field.lit.min.js';
import { css, html, LitElement } from 'lit';
import { property, query, state } from 'lit/decorators.js';
const eventForwarder = (target) => (event) => {
    event.stopPropagation();
    const e = new Event(event.type, event);
    target.dispatchEvent(e);
};
/**
 * `FsCheckbox` implements the Financial Services extension of the Gravity Design System's checkbox component.
 *
 * Be sure to consult with the design guidelines for the button to ensure proper state usage, it is possible to force the button component into an unsupported state.
 *
 * ## Installation
 *
 * ```bash
 * npm i @cof/fs-checkbox-wc
 * ```
 *
 * ## Usage
 *
 * To import the side-effectful registration of `<fs-checkbox>` call
 *
 * ```javascript
 * import '@cof/fs-checkbox-wc/fs-checkbox.js';
 * ```
 *
 * To import the `FsCheckbox` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsCheckbox } from '@cof/fs-checkbox-wc';
 * ```
 *
 * ## Example
 *
 * ```html
 *  <form @submit="${onSubmit}">
 *    <div style="display: flex; gap: 16px; flex-flow: column;">
 *      <fs-checkbox name="fs-checkbox" value="checkbox-one" @change="${onChange}">Checkbox one</fs-checkbox>
 *      <fs-checkbox name="fs-checkbox" value="checkbox-two" @change="${onChange}">Checkbox two</fs-checkbox>
 *      <fs-checkbox name="fs-checkbox" value="checkbox-three" @change="${onChange}">Checkbox three</fs-checkbox>
 *
 *      <fs-button variant="primary" type="submit">Submit</fs-button>
 *    </div>
 *  </form>
 * ```
 *
 * @element fs-checkbox
 * @emits change - Emitted when the form participating value is updated
 * @csspart fs-form-field - The wrapper for checkbox elements
 * @csspart fs-checkbox - The checkbox control
 * @csspart fs-field-label - The checkbox' label
 * @slot - The label text
 */
export class FsCheckbox extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        this._checkboxState = -1;
        /** If true, the input uses compact styles */
        this.compact = fsCheckboxDefaults.compact;
        /** If true, the input is disabled */
        this.disabled = fsCheckboxDefaults.disabled;
        this.formFieldAlignment = 'center';
        this.error = fsCheckboxDefaults.error;
        /** The form control's name */
        this.name = fsCheckboxDefaults.name;
        /** If set this input will be required inside a form */
        this.required = fsCheckboxDefaults.required;
        /** The input's value to reflect back to a form */
        this.value = fsCheckboxDefaults.value;
        this.forwardEvent = eventForwarder(this);
    }
    static get styles() {
        return [checkboxStyles, formFieldStyles, labelStyles, css `:host { display: block; }`];
    }
    /** If true, the input is checked */
    get checked() {
        return this._checkboxState === 1;
    }
    set checked(checked) {
        this._checkboxState = checked ? 1 : -1;
    }
    get indeterminate() {
        return this._checkboxState === 0;
    }
    set indeterminate(indeterminate) {
        this._checkboxState = indeterminate ? 0 : -1;
    }
    render() {
        return html `
      <div class="fs-form-field fs-form-field--inline fs-form-field--alignment-${this.formFieldAlignment}" part="fs-form-field">
        <input
          type="checkbox"
          class="fs-checkbox ${this.compact ? 'fs-checkbox--compact' : ''} ${this.error ? 'fs-checkbox--error' : ''}"
          name="${this.name}"
          id="fs-checkbox"
          .checked="${this.checked}"
          ?disabled="${this.disabled}"
          .indeterminate="${this._checkboxState === 0}"
          ?required="${this.required}"
          value="${this.value}"
          @change="${this._onChange}"
          part="fs-checkbox"
        />
        <label
          class="
            fs-field-label
            fs-field-label--item
            ${this.compact ? 'fs-field-label--compact' : ''}
            ${this.disabled ? 'fs-field-label--disabled' : ''}
          "
          for="fs-checkbox"
          part="fs-field-label"
        >
          <slot></slot>
        </label>
      </div>
    `;
    }
    firstUpdated() {
        this.linkControl(this.renderRoot.querySelector('input'));
        this.addEventListener('invalid', () => {
            this.error = true;
        });
        // Update the checked state of the component if the component is not initially "indeterminate".
        // The native checkbox has no way of setting the intermediate state via attribute, only programatically. In our
        // custom element, it is possible to bind to the property "intermediate" to set it when the component is initially
        // rendered. The closest native analogue here is setting the "intermediate" state programatically after the
        // component is rendered. This would practically override any initial checked state on the element, which is
        // emulated here.
        if (!this.indeterminate) {
            this.checked = this.hasAttribute('checked') || this.checked;
        }
    }
    formResetCallback() {
        this.checked = this.hasAttribute('checked');
    }
    /** Fires when a change event happens on the input */
    _onChange(event) {
        this.checked = event.target.checked;
        this._internals.setFormValue(this.checked ? this.value : null);
        this.forwardEvent(event);
        this.error = !this.validity.valid;
    }
    /** Update the form value if checked or value change */
    updated(changed) {
        if (changed.has('checked') || changed.has('value') || changed.has('_checkboxState')) {
            this._internals.setFormValue(this.checked ? this.value : null);
        }
        if (changed.has('required')) {
            this.reconcileValidity();
        }
    }
}
/** The element's canonical name for use with safe define */
FsCheckbox.canonicalName = 'checkbox';
__decorate([
    state()
], FsCheckbox.prototype, "_checkboxState", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsCheckbox.prototype, "checked", null);
__decorate([
    property({ type: Boolean, reflect: false })
], FsCheckbox.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsCheckbox.prototype, "disabled", void 0);
__decorate([
    property({ type: String, attribute: 'form-field-alignment', reflect: false })
], FsCheckbox.prototype, "formFieldAlignment", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsCheckbox.prototype, "error", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsCheckbox.prototype, "name", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsCheckbox.prototype, "required", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsCheckbox.prototype, "value", void 0);
__decorate([
    query('input')
], FsCheckbox.prototype, "input", void 0);
//# sourceMappingURL=Checkbox.js.map